import React, { useEffect, useState } from "react";
import { Button, Card, Container, Form, Row, Col } from "react-bootstrap";
import {
  Formik,
  Field,
  Form as FormikForm,
  ErrorMessage,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Link, useLocation } from "react-router-dom";

const getBaseUrl = () =>
  process.env.REACT_APP_BASE_URL || window.location.origin || "http://localhost:3000";

const initialValues = {
  user_id: 0,
  title: "",
  origin: "",
  artist: "",
  object_type: "",
  material: "",
  restrict_material: "",
  measurement_units: "",
  height: "",
  width: "",
  depth: "",
  markings: "",
  damage: "",
  repair: "",
  ownership_history: "",
  appraisals: "",
  currency: "",
  price: "",
  year_paid: "",
  main_image: null,
  certificate1: null,
  certificate2: null,
  side_image_1: null,
  side_image_2: null,
  side_image_3: null,
  side_image_4: null,
  categories: "",
  auction_type: "",
  description: "",
};

const fieldMapping = {
  title: "title",
  origin: "item_origin", // API expects item_origin
  artist: "artist",
  object_type: "object_type",
  material: "material",
  restrict_material: "restrict_material",
  measurement_units: "measurement_units",
  height: "height",
  width: "width",
  depth: "depth",
  markings: "markings",
  damage: "damage",
  repair: "repair",
  ownership_history: "ownership_history",
  appraisals: "appraisals",
  currency: "currency",
  price: "price",
  year_paid: "year_paid",
  main_image: "main_image",
  certificate1: "certificate1",
  certificate2: "certificate2",
  side_image_1: "side_image_1",
  side_image_2: "side_image_2",
  side_image_3: "side_image_3",
  side_image_4: "side_image_4",
  categories: "categories", // user requested payload includes categories:1
  auction_type: "auction_type",
  description: "description",
  user_id: "user_id",
};

const createValidationSchema = (isEdit) => {
  const fileImageValidation = Yup.mixed().test(
    "fileType",
    "Unsupported file format (only jpeg/png allowed)",
    (file) => {
      if (!file) return isEdit ? true : false; // required in create, optional in edit
      return file instanceof File && ["image/jpeg", "image/png"].includes(file.type);
    }
  );

  const filePDFOrImageValidation = Yup.mixed().test(
    "fileType2",
    "Unsupported file format (pdf/jpeg/png allowed)",
    (file) => {
      if (!file) return isEdit ? true : false;
      return (
        file instanceof File &&
        ["image/jpeg", "image/png", "application/pdf"].includes(file.type)
      );
    }
  );

  return Yup.object().shape({
    user_id: Yup.number().required("User ID is required").typeError("User ID must be a number"),
    title: Yup.string().required("Title is required"),
    origin: Yup.string().required("Origin is required"),
    artist: Yup.string().required("Artist is required"),
    object_type: Yup.string().required("Object type is required"),
    material: Yup.string().required("Material is required"),
    restrict_material: Yup.string().required("Restricted material is required"),
    measurement_units: Yup.string().required("Measurement units are required"),
    height: Yup.number().typeError("Height must be a number").positive().required("Height is required"),
    width: Yup.number().typeError("Width must be a number").positive().required("Width is required"),
    depth: Yup.number().typeError("Depth must be a number").positive().required("Depth is required"),
    markings: Yup.string().required("Markings are required"),
    damage: Yup.string().required("Damage details are required"),
    repair: Yup.string().required("Repair details are required"),
    ownership_history: Yup.string().required("Ownership history is required"),
    appraisals: Yup.string().required("Appraisals are required"),
    currency: Yup.string().required("Currency is required"),
    price: Yup.number().typeError("Price must be a number").required("Price is required"),
    year_paid: Yup.number()
      .typeError("Year must be a number")
      .min(1000, "Enter a valid year")
      .max(new Date().getFullYear(), "Year cannot be in the future")
      .required("Year acquired is required"),
    categories: Yup.string().required("Category is required"),
    auction_type: Yup.string().required("Auction type is required"),
    description: Yup.string().min(20, "Description must be at least 20 characters").required("Description is required"),
    main_image: fileImageValidation,
    side_image_1: Yup.mixed().test("sideimg", "Unsupported image format", (v) => {
      if (!v) return true;
      return v instanceof File && ["image/jpeg", "image/png"].includes(v.type);
    }),
    side_image_2: Yup.mixed().test("sideimg", "Unsupported image format", (v) => {
      if (!v) return true;
      return v instanceof File && ["image/jpeg", "image/png"].includes(v.type);
    }),
    side_image_3: Yup.mixed().test("sideimg", "Unsupported image format", (v) => {
      if (!v) return true;
      return v instanceof File && ["image/jpeg", "image/png"].includes(v.type);
    }),
    side_image_4: Yup.mixed().test("sideimg", "Unsupported image format", (v) => {
      if (!v) return true;
      return v instanceof File && ["image/jpeg", "image/png"].includes(v.type);
    }),
    certificate1: filePDFOrImageValidation,
    certificate2: filePDFOrImageValidation,
  });
};

const SetUserIdFromToken = () => {
  const { setFieldValue } = useFormikContext();
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setFieldValue("user_id", payload.id || 0);
      } catch (e) {
        console.warn("Failed to decode token for user_id:", e);
      }
    }
  }, [setFieldValue]);
  return null;
};

const LoadEditData = ({ setAuctionId, setExistingImageUrls, isEdit }) => {
  const { setValues } = useFormikContext();
  useEffect(() => {
    if (!isEdit) return;
    const editDataRaw = localStorage.getItem("editAuctionData");
    if (!editDataRaw) return;
    try {
      const parsed = JSON.parse(editDataRaw);
      const auctionId = parsed.id || parsed.auction_id || parsed._id;
      if (auctionId) setAuctionId(auctionId.toString());
      const mapped = {
        user_id: parsed.user_id || 0,
        title: parsed.title || "",
        origin: parsed.item_origin || parsed.origin || "",
        artist: parsed.artist || "",
        object_type: parsed.object_type || "",
        material: parsed.material || "",
        restrict_material: parsed.restrict_material || "",
        measurement_units: parsed.measurement_units || "",
        height: parsed.height?.toString() || parsed.details?.height?.toString() || "",
        width: parsed.width?.toString() || parsed.details?.width?.toString() || "",
        depth: parsed.depth?.toString() || parsed.details?.depth?.toString() || "",
        markings: parsed.markings || parsed.details?.markings || "",
        damage: parsed.damage || parsed.details?.damage || "",
        repair: parsed.repair || parsed.details?.repair || "",
        ownership_history: parsed.ownership_history || parsed.details?.ownership_history || "",
        appraisals: parsed.appraisals || parsed.details?.appraisals || "",
        currency: parsed.currency || parsed.details?.currency || "",
        price: parsed.price?.toString() || parsed.details?.price?.toString() || "",
        year_paid: parsed.year_paid?.toString() || parsed.details?.year_paid?.toString() || "",
        main_image: null,
        certificate1: null,
        certificate2: null,
        side_image_1: null,
        side_image_2: null,
        side_image_3: null,
        side_image_4: null,
        categories: parsed.categories?.toString() || parsed.category_id?.toString() || "",
        auction_type: parsed.auction_type || "",
        description: parsed.description || "",
      };
      const images = {
        main_image: parsed.image_url || parsed.main_image || parsed.image || null,
        certificate1: parsed.certificate1_url || parsed.certificate1 || null,
        certificate2: parsed.certificate2_url || parsed.certificate2 || null,
        side_image_1: parsed.side_image_1_url || parsed.side_image_1 || null,
        side_image_2: parsed.side_image_2_url || parsed.side_image_2 || null,
        side_image_3: parsed.side_image_3_url || parsed.side_image_3 || null,
        side_image_4: parsed.side_image_4_url || parsed.side_image_4 || null,
      };
      setExistingImageUrls(images);
      setValues(mapped);
    } catch (err) {
      console.error("Failed to parse editAuctionData", err);
    }
  }, [isEdit, setAuctionId, setExistingImageUrls, setValues]);
  return null;
};

function UserCreateAuction() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("edit") === "true";

  const [auctionId, setAuctionId] = useState("");
  const [existingImageUrls, setExistingImageUrls] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const base_url = getBaseUrl();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        // console.log("fetching categories");
        const token = await localStorage.getItem("token");
        // console.log(token);
        
        const response = await fetch(`${base_url}/api/categories/list`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : (data.data || []));
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [base_url]);

  // helper: convert existing image URL to file (only used in edit to send same file)
  const urlToFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type || "application/octet-stream" });
    } catch (err) {
      console.warn("urlToFile failed for", url, err);
      return null;
    }
  };

  const handleSubmit = async (values, actions) => {
    // console.log("Submitting values:", values);
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to create an auction");
      actions.setSubmitting(false);
      return;
    }

    const formData = new FormData();

    // Append non-file fields (strings/numbers)
    for (const k of Object.keys(values)) {
      const mappedKey = fieldMapping[k] || k;
      const val = values[k];
      // skip file fields here; they'll be appended below
      if (
        [
          "main_image",
          "certificate1",
          "certificate2",
          "side_image_1",
          "side_image_2",
          "side_image_3",
          "side_image_4",
        ].includes(k)
      ) {
        continue;
      }
      if (val !== undefined && val !== null && val !== "") {
        formData.append(mappedKey, String(val));
        // also add fallback category key if backend expects 'category_id' sometimes
        if (mappedKey === "categories") {
          formData.append("category_id", String(val));
        }
      }
    }

    // Append files and handle existing images when editing
    const imageFields = [
      "main_image",
      "certificate1",
      "certificate2",
      "side_image_1",
      "side_image_2",
      "side_image_3",
      "side_image_4",
    ];

    for (const field of imageFields) {
      const mappedKey = fieldMapping[field];
      const fileValue = values[field];

      if (fileValue instanceof File) {
        // New upload
        formData.append(mappedKey, fileValue);
        // console.log("Appended new file for", field, fileValue.name);
      } else if (isEdit && existingImageUrls[field]) {
        // Convert URL to file and append, else append URL string
        try {
          const filename = existingImageUrls[field].split("/").pop() || `existing_${field}`;
          const converted = await urlToFile(existingImageUrls[field], filename);
          if (converted) {
            formData.append(mappedKey, converted);
            // console.log("Appended converted existing file for", field);
          } else {
            // fallback to sending the URL in a field
            formData.append(`${mappedKey}_url`, existingImageUrls[field]);
            // console.log("Appended existing image URL for", field);
          }
        } catch (err) {
          console.warn("Error processing existing image for", field, err);
          formData.append(`${mappedKey}_url`, existingImageUrls[field]);
        }
      } else {
        // Not present; do nothing (avoid sending empty files unless backend requires)
      }
    }

    // Edit mode: include auction id
    if (isEdit) {
      if (!auctionId) {
        toast.error("Auction ID is missing. Cannot update.");
        actions.setSubmitting(false);
        return;
      }
      formData.append("auction_id", auctionId);
      formData.append("id", auctionId);
      formData.append("updated_at", new Date().toISOString());
    }

    try {
      const endpoint = isEdit
        ? `${base_url}/auction/editAuction/${values.user_id || ""}`
        : `${base_url}/api/auction/createAuction`;

      // console.log("Sending POST to", endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // do NOT set Content-Type for FormData
        },
        body: formData,
      });

      const text = await response.text();
      // try to parse json; fallback to raw text
      let result;
      try {
        result = text ? JSON.parse(text) : {};
      } catch (e) {
        result = { raw: text }; 
      }

      if (!response.ok) {
        console.error("API error:", response.status, result);
        const errMsg = result.message || result.error || result.raw || `HTTP ${response.status}`;
        toast.error(`Failed: ${errMsg}`);
        actions.setSubmitting(false);
        return;
      }

      // console.log("Success response:", result);
      toast.success(isEdit ? "Auction updated successfully!" : "Auction created successfully!");
      actions.resetForm();
      // if editing, clear localStorage edit item
      if (isEdit) localStorage.removeItem("editAuctionData");
    } catch (err) {
      console.error("Submission error:", err);
      toast.error("An unexpected error occurred while submitting the auction");
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <div>
      <Container>
        <div className="d-flex justify-content-between align-items-center p-3 bg-white border-lg mb-3 ">
          <h6>
            {isEdit ? "Edit Auction" : "Create Auction"} {isEdit && auctionId && <small className="text-muted"> (ID: {auctionId})</small>}
          </h6>
          {isEdit && (
            <div className="btn btn-success">
              <Link to="/activeauction">Back</Link>
            </div>
          )}
        </div>

        <Card className="p-4 cus_form_crd">
          <Formik
            initialValues={initialValues}
            validationSchema={createValidationSchema(isEdit)}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, isSubmitting, values }) => (
              <FormikForm>
                <SetUserIdFromToken />
                <LoadEditData setAuctionId={setAuctionId} setExistingImageUrls={setExistingImageUrls} isEdit={isEdit} />

                {/* Basic Info */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Basic Information</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {["title", "origin", "artist", "object_type"].map((field) => (
                        <Col md={6} lg={3} key={field}>
                          <Form.Group controlId={field} className="mb-3">
                            <Form.Label className="fw-medium text-capitalize">
                              {field.replace(/_/g, " ")}
                            </Form.Label>
                            <Field name={field} as={Form.Control} type="text" placeholder={`Enter ${field.replace(/_/g, " ")}`} />
                            <ErrorMessage name={field} component="div" className="text-danger small" />
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Item Details */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Item Details</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {["material", "restrict_material"].map((field) => (
                        <Col md={6} key={field}>
                          <Form.Group controlId={field} className="mb-3">
                            <Form.Label className="fw-medium text-capitalize">{field.replace(/_/g, " ")}</Form.Label>
                            <Field name={field} as={Form.Control} type="text" placeholder={`Enter ${field.replace(/_/g, " ")}`} />
                            <ErrorMessage name={field} component="div" className="text-danger small" />
                          </Form.Group>
                        </Col>
                      ))}

                      <Col md={6}>
                        <Form.Group controlId="measurement_units" className="mb-3">
                          <Form.Label className="fw-medium">Measurement Units</Form.Label>
                          <Field name="measurement_units" as={Form.Select}>
                            <option value="">Select unit</option>
                            <option value="cm">Centimeters (cm)</option>
                            <option value="in">Inches (in)</option>
                            <option value="mm">Millimeters (mm)</option>
                            <option value="m">Meters (m)</option>
                          </Field>
                          <ErrorMessage name="measurement_units" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Dimensions */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Dimensions</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {["height", "width", "depth"].map((field) => (
                        <Col md={4} key={field}>
                          <Form.Group controlId={field} className="mb-3">
                            <Form.Label className="fw-medium text-capitalize">{field}</Form.Label>
                            <div className="input-group">
                              <Field name={field} as={Form.Control} type="number" step="0.01" placeholder={`Enter ${field}`} />
                              <span className="input-group-text">{values.measurement_units || "units"}</span>
                            </div>
                            <ErrorMessage name={field} component="div" className="text-danger small" />
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Condition & History */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Condition & History</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {["markings", "damage", "repair", "ownership_history", "appraisals"].map((field) => (
                        <Col md={field === "appraisals" || field === "ownership_history" ? 12 : 6} key={field}>
                          <Form.Group controlId={field} className="mb-3">
                            <Form.Label className="fw-medium text-capitalize">{field.replace(/_/g, " ")}</Form.Label>
                            <Field
                              name={field}
                              as={field === "appraisals" || field === "ownership_history" ? "textarea" : Form.Control}
                              className={field === "appraisals" || field === "ownership_history" ? "form-control" : ""}
                              rows={field === "appraisals" || field === "ownership_history" ? 3 : undefined}
                              placeholder={`Enter ${field.replace(/_/g, " ")}`}
                            />
                            <ErrorMessage name={field} component="div" className="text-danger small" />
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Pricing */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Pricing Information</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group controlId="currency" className="mb-3">
                          <Form.Label className="fw-medium">Currency</Form.Label>
                          <Field name="currency" as={Form.Select}>
                            <option value="">Select currency</option>
                            <option value="INR">INR</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            {/* add more as needed */}
                          </Field>
                          <ErrorMessage name="currency" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group controlId="price" className="mb-3">
                          <Form.Label className="fw-medium">Price</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">{values.currency || "USD"}</span>
                            <Field name="price" as={Form.Control} type="number" step="0.01" placeholder="Enter price" />
                          </div>
                          <ErrorMessage name="price" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group controlId="year_paid" className="mb-3">
                          <Form.Label className="fw-medium">Year Acquired</Form.Label>
                          <Field name="year_paid" as={Form.Control} type="number" min="1000" max={new Date().getFullYear()} placeholder="e.g., 2010" />
                          <ErrorMessage name="year_paid" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group controlId="auction_type" className="mb-3">
                          <Form.Label className="fw-medium">Auction Type</Form.Label>
                          <Field name="auction_type" as={Form.Select}>
                            <option value="">Select auction type</option>
                            <option value="public">Public Auction</option>
                            <option value="private">Private Sale</option>
                          </Field>
                          <ErrorMessage name="auction_type" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Description */}
                    <Form.Group controlId="description" className="mb-3">
                      <Form.Label className="fw-medium">Item Description</Form.Label>
                      <Field name="description" as="textarea" className="form-control" rows={4} placeholder="Describe the item..." />
                      <small className="text-muted">Include provenance, condition, and special features.</small>
                      <ErrorMessage name="description" component="div" className="text-danger small" />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Images Section */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Item Images</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Main Image (jpeg/png) <span className="text-danger">*</span></Form.Label>
                          <input
                            name="main_image"
                            type="file"
                            accept="image/png, image/jpeg"
                            className="form-control"
                            onChange={(e) => {
                              setFieldValue("main_image", e.currentTarget.files?.[0] || null);
                            }}
                          />
                          <ErrorMessage name="main_image" component="div" className="text-danger small" />
                          {existingImageUrls.main_image && (
                            <div className="mt-2">
                              <small className="text-muted">Current: </small>
                              <a href={existingImageUrls.main_image} target="_blank" rel="noreferrer">
                                View Image
                              </a>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      {[1, 2, 3, 4].map((num) => (
                        <Col md={6} key={`side-image-${num}`}>
                          <Form.Group className="mb-3">
                            <Form.Label>Side Image {num} (jpeg/png)</Form.Label>
                            <input
                              name={`side_image_${num}`}
                              type="file"
                              accept="image/png, image/jpeg"
                              className="form-control"
                              onChange={(e) => setFieldValue(`side_image_${num}`, e.currentTarget.files?.[0] || null)}
                            />
                            <ErrorMessage name={`side_image_${num}`} component="div" className="text-danger small" />
                            {existingImageUrls[`side_image_${num}`] && (
                              <div className="mt-2">
                                <small className="text-muted">Current: </small>
                                <a href={existingImageUrls[`side_image_${num}`]} target="_blank" rel="noreferrer">
                                  View Image {num}
                                </a>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Certificates Section */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Certificates</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {[1, 2].map((num) => (
                        <Col md={6} key={`cert-${num}`}>
                          <Form.Group className="mb-3">
                            <Form.Label>Certificate {num} (pdf/jpeg/png)</Form.Label>
                            <input
                              name={`certificate${num}`}
                              type="file"
                              accept="image/png, image/jpeg, application/pdf"
                              className="form-control"
                              onChange={(e) => setFieldValue(`certificate${num}`, e.currentTarget.files?.[0] || null)}
                            />
                            <ErrorMessage name={`certificate${num}`} component="div" className="text-danger small" />
                            {existingImageUrls[`certificate${num}`] && (
                              <div className="mt-2">
                                <small className="text-muted">Current: </small>
                                <a href={existingImageUrls[`certificate${num}`]} target="_blank" rel="noreferrer">
                                  View Certificate {num}
                                </a>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>

                {/* Category Section */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Category</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="categories" className="mb-3">
                          <Form.Label className="fw-medium">Category</Form.Label>
                          <Field name="categories" as={Form.Select}>
                            <option value="">Select category</option>
                            {loadingCategories ? (
                              <option value="">Loading...</option>
                            ) : (
                              categories.map((c) => (
                                <option key={c.id ?? c._id ?? c.category_id} value={c.id ?? c._id ?? c.category_id}>
                                  {c.name || c.category_name || `Category ${c.id ?? c._id ?? c.category_id}`}
                                </option>
                              ))
                            )}
                          </Field>
                          <ErrorMessage name="categories" component="div" className="text-danger small" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <div className="mt-4 d-flex justify-content-end">
                  <Button
                    type="submit"
                    className="btn-golden-glass"
                    disabled={isSubmitting || (isEdit && !auctionId)}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEdit ? "Updating..." : "Submitting..."}
                      </>
                    ) : isEdit ? "Update" : "Submit"}
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </Card>
      </Container>
    </div>
  );
}

export default UserCreateAuction;